import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Github, PlugZap, ShieldCheck, Trash2, PlusCircle, Upload, ChevronDown, ChevronRight, Check } from 'lucide-react';
import { githubApi, githubScanApi, engagementsApi, ApiError } from '../lib/api-client';
import { useAuth } from '../lib/auth-context';
import { can } from '../lib/rbac';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge, SeverityBadge } from '../components/ui/badge';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Input, Label, FieldHint } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { GITHUB_INSTALL_STATE_KEY } from '../lib/github-install-state';
import type { GitHubScanFinding, GitHubScanRunResult } from '../lib/types';

const CHECK_LABEL: Record<string, string> = {
  'github.secret-scanning': 'Secret Scanning',
  'github.dependabot': 'Dependabot',
  'github.code-scanning': 'Code Scanning',
  'github.exposed-filename': 'Exposed Filename',
};

const REQUIRED_TOKEN_SCOPES = ['repo', 'security_events', 'read:org'];

const CREATE_TOKEN_URL = 'https://github.com/settings/tokens/new?scopes=repo,security_events,read:org&description=DefendIQ+Code+Scanning';

const SCAN_CHECKS = ['Exposed secrets and API keys', 'Vulnerable dependencies (CVEs)', 'Code security alerts (SAST)', 'Sensitive files in recent commits'];

/** Collapsed/expanded state is remembered per-browser, but defaults to OPEN the first time this
 * page is ever visited (no stored value yet) — so a first-time user always sees the setup steps,
 * while someone who's already dismissed it isn't shown it again on every visit. Same
 * try/catch-around-localStorage posture as AppShell.tsx's nav-section persistence. */
const SETUP_GUIDE_STORAGE_KEY = 'defendiq_github_setup_guide_open';

function readSetupGuideOpen(): boolean {
  try {
    const stored = window.localStorage.getItem(SETUP_GUIDE_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

function writeSetupGuideOpen(open: boolean): void {
  try {
    window.localStorage.setItem(SETUP_GUIDE_STORAGE_KEY, open ? 'true' : 'false');
  } catch {
    // localStorage unavailable (e.g. private mode) — fail open to in-memory state only.
  }
}

/** Shape of GET https://api.github.com/user — only the one field the "Test connection" check needs. */
interface GitHubUser {
  login: string;
}

/** Best available human-readable location for a scan finding — evidence shapes differ per check
 * (a file path, a dependency package name, a raw filename), so this tries each in turn. */
function findingLocation(finding: GitHubScanFinding): string {
  const evidence = finding.evidence;
  if (typeof evidence.path === 'string') return evidence.path;
  if (typeof evidence.filename === 'string') return evidence.filename;
  if (typeof evidence.package === 'string') return evidence.package;
  return finding.target;
}

/**
 * Walkthrough above the scan form: how to mint a correctly-scoped GitHub token, the repo URL
 * format, and what a scan actually checks. Open by default the first time a user visits this page
 * (see `readSetupGuideOpen`'s docstring); collapsible and remembers whatever the user last set
 * after that. Self-contained (owns its own open/closed state) — nothing else on the page needs to
 * know whether it's expanded.
 */
function SetupGuide() {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setOpen(readSetupGuideOpen());
  }, []);

  const toggle = () => {
    setOpen((v) => {
      writeSetupGuideOpen(!v);
      return !v;
    });
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 p-6">
        <div>
          <h3 className="text-base font-semibold text-ink">How to connect your GitHub repository</h3>
          <p className="text-sm text-ink-muted">New here? Follow these steps to create a token and run your first scan.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={toggle} aria-expanded={open}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {open ? 'Hide guide' : 'Setup guide'}
        </Button>
      </div>
      {open ? (
        <CardContent className="space-y-6 pt-0">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-ink">Step 1 — Create a Personal Access Token (PAT)</h4>
            <p className="text-sm text-ink-muted">
              Go to GitHub → Settings → Developer settings → Personal access tokens → Tokens (classic) → Generate new token
              (classic).
            </p>
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-faint">Required scopes</p>
              <div className="flex flex-wrap gap-1.5">
                {REQUIRED_TOKEN_SCOPES.map((scope) => (
                  <Badge key={scope} variant="success" className="gap-1 font-mono">
                    <Check size={12} /> {scope}
                  </Badge>
                ))}
              </div>
            </div>
            <p className="text-sm text-ink-muted">Recommended expiry: 90 days.</p>
            <Button asChild variant="outline" size="sm">
              <a href={CREATE_TOKEN_URL} target="_blank" rel="noreferrer">
                Create token on GitHub →
              </a>
            </Button>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-ink">Step 2 — Enter your repository URL</h4>
            <p className="text-sm text-ink-muted">Format: https://github.com/your-org/your-repo</p>
            <p className="font-mono text-xs text-ink-faint">Example: https://github.com/acme/api-service</p>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-ink">Step 3 — Click "Scan Repository"</h4>
            <p className="text-sm text-ink-muted">DefendIQ will check for:</p>
            <ul className="space-y-1 text-sm text-ink-muted">
              {SCAN_CHECKS.map((check) => (
                <li key={check} className="flex items-start gap-2">
                  <span aria-hidden="true">•</span> {check}
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      ) : null}
    </Card>
  );
}

/**
 * The PAT-based scanning tab (Feature: GitHub Code Scanning). Deliberately separate from the
 * GitHub App installation flow below it — the PAT is entered fresh for every scan, sent once to
 * `POST /v1/github-scan/run`, and never stored anywhere (client or server).
 */
function CodeScanningTab() {
  const { toast } = useToast();
  const [pat, setPat] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [result, setResult] = useState<GitHubScanRunResult | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [engagementId, setEngagementId] = useState('');

  const engagementsQuery = useQuery({ queryKey: ['engagements'], queryFn: () => engagementsApi.list(), enabled: exportOpen });

  const scanMutation = useMutation({
    mutationFn: () => githubScanApi.run({ repoUrl, pat }),
    onSuccess: (scanResult) => {
      setResult(scanResult);
      setPat('');
      if (scanResult.rateLimited) {
        toast({
          title: 'Scan stopped early',
          description: 'GitHub API rate limit ran low — not all checks completed.',
          variant: 'error',
        });
      }
    },
    onError: (err) => {
      setPat('');
      toast({ title: 'Scan failed', description: friendlyErrorMessage(err), variant: 'error' });
    },
  });

  /**
   * Calls GitHub's API directly from the browser with the pasted token — never routed through our
   * own backend, so this can't be mistaken for "sending the token to DefendIQ" and doesn't touch
   * apps/api at all. GitHub's REST API serves CORS headers on authenticated requests specifically
   * so this kind of client-side check works.
   */
  const testConnectionMutation = useMutation({
    mutationFn: async (): Promise<GitHubUser> => {
      const res = await fetch('https://api.github.com/user', {
        headers: { Authorization: `Bearer ${pat}`, Accept: 'application/vnd.github+json' },
      });
      if (res.status === 401) throw new Error('Invalid token.');
      if (!res.ok) throw new Error(`GitHub returned an unexpected error (HTTP ${res.status}).`);
      return (await res.json()) as GitHubUser;
    },
  });

  const exportMutation = useMutation({
    mutationFn: () => githubScanApi.export({ findings: result!.findings, engagementId }),
    onSuccess: (exportResult) => {
      toast({
        title: 'Exported to findings',
        description: `${exportResult.findings.length} finding${exportResult.findings.length === 1 ? '' : 's'} added.`,
        variant: 'success',
      });
      setExportOpen(false);
      setEngagementId('');
    },
    onError: (err) => toast({ title: 'Could not export', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const handleScan = (e: FormEvent) => {
    e.preventDefault();
    setResult(null);
    scanMutation.mutate();
  };

  return (
    <div className="space-y-4">
      <SetupGuide />

      <Card>
        <CardContent className="pt-6">
          <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleScan}>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="scan-repo-url">Repository URL</Label>
              <Input
                id="scan-repo-url"
                value={repoUrl}
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="https://github.com/org/repo"
                required
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="scan-pat">Personal access token</Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="scan-pat"
                  type="password"
                  value={pat}
                  onChange={(e) => {
                    setPat(e.target.value);
                    testConnectionMutation.reset();
                  }}
                  placeholder="ghp_..."
                  autoComplete="off"
                  required
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => testConnectionMutation.mutate()}
                  disabled={!pat.trim() || testConnectionMutation.isPending}
                >
                  <PlugZap size={14} /> {testConnectionMutation.isPending ? 'Testing…' : 'Test connection'}
                </Button>
              </div>
              <FieldHint>Your token is used only for this scan request and is never stored by DefendIQ.</FieldHint>
              {testConnectionMutation.isSuccess ? (
                <p className="text-xs text-success">✓ Connected as: {testConnectionMutation.data.login}</p>
              ) : testConnectionMutation.isError ? (
                <p className="text-xs text-danger">
                  ✗ {testConnectionMutation.error instanceof Error ? testConnectionMutation.error.message : 'Could not connect to GitHub.'}
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={scanMutation.isPending || !repoUrl.trim() || !pat.trim()}>
                <Github size={16} /> Scan Repository
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {scanMutation.isPending ? (
        <LoadingState />
      ) : result === null ? (
        <EmptyState
          icon={<Github size={32} />}
          title="Scan a repository"
          description="Connect a GitHub repository to scan for exposed secrets, vulnerable dependencies, and security alerts."
        />
      ) : result.findings.length === 0 ? (
        <EmptyState icon={<ShieldCheck size={28} />} title="No issues found" description="This repository looks clean across all checks that ran." />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-ink-muted">
              {result.findings.length} finding{result.findings.length === 1 ? '' : 's'}
              {result.rateLimited ? ' · scan stopped early (rate limit)' : ''}
            </p>
            <Dialog open={exportOpen} onOpenChange={setExportOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Upload size={14} /> Export to Findings
                </Button>
              </DialogTrigger>
              <DialogContent title="Export to an engagement" description="Choose which engagement these findings should be attached to.">
                <div className="space-y-4">
                  <Select value={engagementId} onValueChange={setEngagementId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an engagement" />
                    </SelectTrigger>
                    <SelectContent>
                      {(engagementsQuery.data?.engagements ?? []).map((engagement) => (
                        <SelectItem key={engagement.id} value={engagement.id}>
                          {engagement.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button className="w-full" disabled={!engagementId || exportMutation.isPending} onClick={() => exportMutation.mutate()}>
                    Export
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-ink-faint">
                  <th className="p-3 font-medium">Severity</th>
                  <th className="p-3 font-medium">Check</th>
                  <th className="p-3 font-medium">Title</th>
                  <th className="p-3 font-medium">Location</th>
                  <th className="p-3 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {result.findings.map((finding, index) => (
                  <tr key={index} className="border-b border-border last:border-0">
                    <td className="p-3">
                      <SeverityBadge severity={finding.severity} />
                    </td>
                    <td className="p-3 text-ink-muted">{CHECK_LABEL[finding.moduleId] ?? finding.moduleId}</td>
                    <td className="p-3 font-medium text-ink">{finding.title}</td>
                    <td className="p-3 font-mono text-xs text-ink-muted">{findingLocation(finding)}</td>
                    <td className="p-3 text-ink-muted">
                      {typeof finding.evidence.htmlUrl === 'string' ? (
                        <a href={finding.evidence.htmlUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                          View on GitHub
                        </a>
                      ) : (
                        <span className="line-clamp-1" title={finding.description}>
                          {finding.description}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Connection happens via the GitHub App install flow (docs/github-enterprise.md): a redirect to
 * GitHub to pick/confirm the org, then a callback (see GitHubCallback.tsx) that completes the
 * connection — never a pasted personal-access-token. That's a deliberate security choice, not a
 * missing feature: the App mints short-lived installation tokens on demand server-side and never
 * stores (or could echo back) a long-lived credential, the same never-return-a-secret posture
 * apiKeysApi already has for API keys.
 */
function IssueExportTab() {
  const { toast } = useToast();
  const { principal } = useAuth();
  const queryClient = useQueryClient();
  const canManage = principal?.role === undefined || can(principal.role, 'githubIntegration:manage');

  const installationQuery = useQuery({ queryKey: ['github', 'installation'], queryFn: () => githubApi.installation() });
  const installation = installationQuery.data?.installation ?? null;
  const connected = installation !== null && installation.status === 'active';

  const startMutation = useMutation({
    mutationFn: () => githubApi.installStart(),
    onSuccess: (result) => {
      sessionStorage.setItem(GITHUB_INSTALL_STATE_KEY, result.installState);
      window.location.href = result.url;
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'GITHUB_NOT_CONFIGURED') {
        toast({
          title: 'GitHub isn’t set up for this deployment yet',
          description: 'Ask your DefendIQ administrator to configure the GitHub App before connecting an organization.',
          variant: 'error',
        });
        return;
      }
      toast({ title: 'Could not start the GitHub connection', description: friendlyErrorMessage(err), variant: 'error' });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => githubApi.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['github'] });
      toast({ title: 'GitHub organization disconnected', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not disconnect', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const discoveredQuery = useQuery({
    queryKey: ['github', 'repos', 'discovered'],
    queryFn: () => githubApi.discoveredRepos(),
    enabled: connected,
  });
  const allowlistQuery = useQuery({
    queryKey: ['github', 'repos', 'allowlist'],
    queryFn: () => githubApi.allowlist(),
    enabled: connected,
  });
  const allowlistedKeys = new Set((allowlistQuery.data?.repos ?? []).map((r) => `${r.owner}/${r.repo}`));

  const addToAllowlistMutation = useMutation({
    mutationFn: (input: { owner: string; repo: string }) => githubApi.addToAllowlist(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['github', 'repos', 'allowlist'] });
      toast({ title: 'Repository added', description: 'Findings can now be exported to this repo.', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not add repository', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const removeFromAllowlistMutation = useMutation({
    mutationFn: (id: string) => githubApi.removeFromAllowlist(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['github', 'repos', 'allowlist'] });
      toast({ title: 'Repository removed', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not remove repository', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  if (installationQuery.isLoading) return <LoadingState />;
  if (installationQuery.isError) return <ErrorState error={installationQuery.error} onRetry={() => installationQuery.refetch()} />;

  return !connected ? (
    <EmptyState
      icon={<Github size={32} />}
      title={installation?.status === 'suspended' ? 'Installation suspended' : installation?.status === 'removed' ? 'Installation removed' : 'Not connected yet'}
      description={
        installation
          ? `The GitHub App installation for ${installation.accountLogin} is ${installation.status}. Reconnect from GitHub to resume exporting findings.`
          : 'Connect a GitHub organization to export findings as Issues.'
      }
      action={
        canManage ? (
          <Button onClick={() => startMutation.mutate()} disabled={startMutation.isPending}>
            <PlugZap size={16} /> Connect to GitHub
          </Button>
        ) : undefined
      }
    />
  ) : (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
          <div className="flex items-center gap-3">
            <ShieldCheck size={20} className="text-success" />
            <div>
              <p className="font-medium text-ink">{installation.accountLogin}</p>
              <p className="text-xs text-ink-muted">Connected {new Date(installation.createdAt).toLocaleDateString()}</p>
            </div>
            <Badge variant="success">Connected</Badge>
          </div>
          {canManage ? (
            <ConfirmDialog
              trigger={
                <Button variant="ghost" size="sm">
                  <Trash2 size={14} className="text-danger" /> Disconnect
                </Button>
              }
              title="Disconnect this organization?"
              description={`Findings can no longer be exported to ${installation.accountLogin} until it's reconnected. Allowlisted repos are removed too.`}
              confirmLabel="Disconnect"
              variant="danger"
              onConfirm={() => disconnectMutation.mutateAsync()}
            />
          ) : null}
        </CardContent>
      </Card>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-ink">Allowlisted repositories</h3>
        <p className="mb-3 text-xs text-ink-muted">Only repos listed here can receive exported findings — nothing is indexed until it's added.</p>
        {allowlistQuery.isLoading ? (
          <LoadingState />
        ) : (allowlistQuery.data?.repos ?? []).length === 0 ? (
          <EmptyState icon={<Github size={24} />} title="No repositories allowlisted" description="Add one from the discovered repos below." />
        ) : (
          <div className="space-y-2">
            {(allowlistQuery.data?.repos ?? []).map((repo) => (
              <Card key={repo.id}>
                <CardContent className="flex items-center justify-between pt-6">
                  <p className="font-mono text-sm text-ink">
                    {repo.owner}/{repo.repo}
                  </p>
                  {canManage ? (
                    <Button variant="ghost" size="icon" aria-label="Remove from allowlist" onClick={() => removeFromAllowlistMutation.mutate(repo.id)}>
                      <Trash2 size={16} className="text-danger" />
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {canManage ? (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-ink">Discovered repositories</h3>
          <p className="mb-3 text-xs text-ink-muted">Visible to the installation — add a repo to the allowlist to enable exports to it.</p>
          {discoveredQuery.isLoading ? (
            <LoadingState />
          ) : discoveredQuery.isError ? (
            <ErrorState error={discoveredQuery.error} onRetry={() => discoveredQuery.refetch()} />
          ) : (discoveredQuery.data?.repos ?? []).length === 0 ? (
            <EmptyState icon={<Github size={24} />} title="No repositories visible" description="Grant this GitHub App access to repositories from GitHub's installation settings." />
          ) : (
            <div className="space-y-2">
              {(discoveredQuery.data?.repos ?? [])
                .filter((repo) => !allowlistedKeys.has(`${repo.owner}/${repo.repo}`))
                .map((repo) => (
                  <Card key={`${repo.owner}/${repo.repo}`}>
                    <CardContent className="flex items-center justify-between gap-3 pt-6">
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-sm text-ink">
                          {repo.owner}/{repo.repo}
                        </p>
                        {repo.private ? <Badge variant="neutral">Private</Badge> : null}
                      </div>
                      <Button size="sm" onClick={() => addToAllowlistMutation.mutate({ owner: repo.owner, repo: repo.repo })} disabled={addToAllowlistMutation.isPending}>
                        <PlusCircle size={14} /> Add to allowlist
                      </Button>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Issue Export (the GitHub App install flow) is no longer a visible tab — it's a much less common
 * path than Code Scanning, so it's tucked behind a small text link at the bottom of the page,
 * hidden until clicked, rather than competing for attention as an equally-weighted tab. Its own
 * content/logic (`IssueExportTab`) is unchanged; only how a user reaches it changed.
 */
function IssueExportDisclosure() {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <div className="border-t border-border pt-4 text-center">
        <button type="button" onClick={() => setOpen(true)} className="text-sm font-medium text-brand hover:underline">
          Looking for GitHub Issues export? →
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 border-t border-border pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-ink">Issue Export</h3>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-ink-muted hover:underline">
          Hide
        </button>
      </div>
      <IssueExportTab />
    </div>
  );
}

/**
 * Code Scanning (PAT-based, read-only) is the page's only visible content by default — Issue
 * Export (the GitHub App install flow) lives behind `IssueExportDisclosure`'s collapsed link
 * below it, since it's a much less commonly used path. Both previously lived on this route as
 * equally-weighted tabs; see nav cleanup brief for why that changed.
 */
export function GitHubEnterprise() {
  return (
    <div className="space-y-6">
      <PageHeader title="GitHub Code Scanning" description="Scan repositories for exposed secrets, vulnerable dependencies, and code security alerts." />

      <CodeScanningTab />

      <IssueExportDisclosure />
    </div>
  );
}
